<template lang="pug">
    .play-box.numerical-network-game-container
        .play-box__game(v-if='!isLoading')
            game-header(
                @soundStateChanged="headerKey = !headerKey",
                :key="headerKey",
                :slug="type",
                game-type="mathMagic",
                :is-homework="!!exerciseId",
            )
            .numerical-network-game-container__divider
            .numerical-network-game-container__interactive-place(v-show='gameStarted && !gameFinished')
                .numerical-network-game-container__game(v-show='isRunning')
                    .numerical-network-game-container__game-info
                        game-info(
                            :score="{left: answers.good, right: answers.bad}"
                            :time="getTime"
                            :parameters="parameters"
                            :game-allows-tips="true"
                        )
                    .numerical-network-game-container__game-box(v-show='showItem')
                        .numerical-network-game-container__game-box__centralPart
                            | {{ getCorrectAnswer}}
                    .numerical-network-game-container__answer-form(v-if='showAnswerForm')
                        .numerical-network-game-container__answer-header
                            | {{ $i18n.t('trainer.trainer_gamePlayBox_enterTheAnswer') }}
                        .numerical-network-game-container__answer-body
                            .numerical-network-game-container__input
                                cs-input(
                                    ref='input',
                                    @keyup.native.enter='processReply',
                                    v-model='answer',
                                    v-focus="true"
                                )
                        .numerical-network-game-container__answer-footer
                            cs-button(@click='processReply', color="blue", type="button")
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
                .numerical-network-game-container__reaction-container(v-if='!isRunning')
                    result-image.numerical-network-game-container__reaction-image(:status="correct ? 'success' : 'failure'")
                    template(v-if='!gameFinished')
                        cs-button(
                            v-if='correct',
                            v-focus='true',
                            @click='refreshGame',
                            color="blue",
                        )
                            | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
                        cs-button(
                            v-else,
                            v-focus='true',
                            @click='refreshGame',
                            color="blue",
                        )
                            | {{ $i18n.t('trainer.trainer_games_tryAgain') }}
            template(v-if='gameFinished')
                .numerical-network-game-container__reaction-container
                    .numerical-network-game-container__reaction
                        .numerical-network-game-container__reaction-side.numerical-network-game-container__reaction-side--correct
                            div
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_correct') }}
                            .numerical-network-game-container__reaction-count
                                | {{ answers.good }}
                        .numerical-network-game-container__reaction-image-container
                            result-image.numerical-network-game-container__reaction-image(:status="'success'")
                        .numerical-network-game-container__reaction-side
                            div
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_wrong') }}
                            .numerical-network-game-container__reaction-count
                                | {{ answers.bad }}
                    result-buttons(
                        :exercise-id="exerciseId"
                        games-type="mathMagic"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
        cs-loader(v-else)
</template>

<script>
    import { random, cloneDeep } from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';

    function DEFAULT_DATA() {
        return {
            isRunning: true,
            answer: '',
            correctAnswer: null,
            correct: false,
            showItem: true,
            showAnswerForm: false,
            isSingleScreen: true,
            isFocused: false,
            level: null,
            maxLevel: 19,
            health: 3,
            answers: {
                good: 0,
                bad: 0,
            },
        };
    }

    export default {
        mixins: [playBoxGameMixin],
        data() {
            return DEFAULT_DATA();
        },
        computed: {
            getCorrectAnswer() {
                return this.correctAnswer;
            },
        },
        watch: {
            isRunning: {
                handler(val) {
                    this.$emit('checkAnswersReady', val);
                },
                immediate: true,
            },
            level: {
                handler(val) {
                    if (val > this.maxLevel) {
                        this.finishGame();
                    } else {
                        this.generateNumbers();
                    }
                },
            },
            health: {
                handler(val) {
                    if (val === 0) {
                        this.finishGame();
                    }
                },
            },
        },
        updated() {
            if (!this.isFocused && this.$refs.input) {
                this.$refs.input.focus();
                this.isFocused = true;
            }
        },
        methods: {
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.assignDataFromParameters();
                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);
                        this.beforeStart();
                        this.isLoading = false;
                        this.generateNumbers();
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.startGame();
            },
            startGame() {
                this.gameStarted = true;
                this.startTimer();
                setTimeout(() => {
                    this.showItem = false;
                    this.showAnswerForm = true;
                    if (this.answerDuration) {
                        this.startAnswerTimer();
                    }
                }, this.parameters.speed * 1000);
            },
            startTimer() {
                this.$nextTick(() => {
                    if (!this.timer) {
                        this.setTimer();
                    }
                });
            },
            assignDataFromParameters() {
                this.level = this.parameters.difficult.value - 1;
            },
            processReply() {
                if (this.answer !== '') {
                    this.stopTimer();
                    if (this.answer === this.correctAnswer) {
                        this.correct = true;
                        this.answers.good++;
                        this.health = 3;
                        this.level++;
                        this.playResultSound('success');
                    } else {
                        this.playResultSound('failure');
                        this.answers.bad++;
                        this.health--;
                        this.generateNumbers();
                    }
                    this.isRunning = false;
                }
            },
            generateNumbers() {
                if (this.level < 16) {
                    this.correctAnswer = this.getRandomInt(
                        this.getMaxMinValueOfNumber(this.level + 1)
                    ).toString();
                } else {
                    this.correctAnswer =
                        this.getRandomInt(this.getMaxMinValueOfNumber(9)).toString() +
                        this.getRandomInt(this.getMaxMinValueOfNumber(this.level - 8)).toString();
                }
            },
            getMaxMinValueOfNumber(n) {
                let maxValue = Math.pow(10, n) - 1;
                let minValue = Math.pow(10, n - 1);
                return [minValue, maxValue];
            },
            getRandomInt([min, max]) {
                return random(min, max);
            },
            refreshGame() {
                this.setTimer();
                this.isFocused = false;
                this.correct = null;
                this.answer = '';
                this.isRunning = true;
                this.showItem = true;
                this.showAnswerForm = false;
                this.showItemTimeout = setTimeout(() => {
                    this.showItem = false;
                    this.showAnswerForm = true;
                    if (this.answerDuration) {
                        this.startAnswerTimer();
                    }
                }, this.parameters.speed * 1000);
            },
            finishGame() {
                this.gameFinished = true;
                this.stopTimer();
                this.sendStat();
            },
            playResultSound(result) {
                this.resultInstance = this.$createjs.Sound.play(result);
                this.resultInstance.volume = 0.6;
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.answers.good + this.answers.bad;
                this.gameStat.answers = this.answers.good;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';
    .play-box__game {
        display: flex;
        justify-content: center;
        flex: 1 1 auto;
    }

    .buttons {
        text-align: center;
        padding-bottom: 60px;
    }

    .numerical-network-game-container {
        width: 100%;
        display: flex;
        overflow: hidden;
        &_with-warning {
            display: table;
            width: 100%;
            height: 100%;
        }
        &__result-container {
            padding: 60px;
            display: flex;
            flex-direction: column;
            align-items: center;
            &__info {
                margin: 20px 0px;
            }
        }
        &__result-image {
            max-height: 360px;
            max-width: 360px;
            @include media($sm) {
                max-width: 450px;
                max-height: 450px;
            }
            vertical-align: middle;
        }
        &__interactive-place {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            text-align: center;
            flex-wrap: wrap;
            flex-grow: 1;
            flex-shrink: 1;
            @include inset-space($space-base);
        }
        &__game {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
        &__game-info {
            display: flex;
            align-self: flex-end;
        }
        &__game-box {
            min-width: 180px;
            display: flex;
            justify-content: center;
            align-items: center;
            @include text($font-size-44, $font-weight-bold);
            &__centralPart {
                font-size: 25px;
                margin-bottom: 8px;
                @include media($lg) {
                    font-size: 50px;
                }
            }
        }
        &__answer-form {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }
        &__answer-header {
            font-weight: 500;
            font-size: 32px;
            line-height: 38px;
            text-align: center;
            color: #333333;
            @include stack-space($space-small);
        }
        &__answer-body {
            @include stack-space($space-small);
        }
        &__input {
            .cs-input {
                width: 320px;
                height: 50px;
                text-align: center;
                @include text($font-size-24, $font-weight-bold);
                @include media($md) {
                    @include text($font-size-24, $font-weight-bold);
                    width: 360px;
                    height: 60px;
                }
                @include media($lg) {
                    width: 400px;
                    height: 80px;
                    @include text($font-size-28, $font-weight-bold);
                }
                -moz-appearance: textfield;
                &::-webkit-inner-spin-button,
                &::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                    margin: 0;
                }
            }
        }
        &__game-turtle {
            display: none;
            position: fixed;
            bottom: $space-large;
            right: $space-large;
            left: auto;
            width: 125px;
            @include media($lg) {
                display: block;
            }
            @include media($xlg) {
                width: 175px;
            }
        }
        &__divider {
            border-bottom: 1px solid $color-grey-300;
        }
        &__reaction-image {
            @include stack-space($space-base);
            max-height: 360px;
            @include media($sm) {
                max-height: unset;
            }
        }
        &__reaction-image-container {
            display: none;
            @include media($md) {
                display: unset;
            }
        }
        &__reaction-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            flex-shrink: 1;
        }
        &__footer-buttons {
            display: flex;
        }
        &__reaction {
            display: flex;
            align-items: center;
            justify-content: space-around;
            text-align: center;
            width: 100%;
            flex-direction: column;
            @include stack-space($space-base);
            @include media($md) {
                flex-direction: row;
            }
        }
        &__reaction-side {
            font-weight: 500;
            font-size: 22px;
            line-height: 50px;
            text-align: center;
            color: #ff3d53;
            @include media($lg) {
                font-size: 34px;
            }
            @include media($xlg) {
                font-size: 34px;
            }
            @include make-col(12, 12, 0);
            @include stack-space($space-base);
            @include media($sm) {
                @include make-col(5, 12, 0);
                @include stack-space(0);
            }
            &--correct {
                color: #6bbd6c;
            }
        }
        &__reaction-count {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            @include text($font-size-40, $font-weight-bold);
            @include media($lg) {
                @include text(70px, $font-weight-bold);
            }
            @include media($xlg) {
                @include text(94px, $font-weight-bold);
            }
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .router-link {
        margin: 5px;
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        @include media($sm) {
            margin-top: 25px;
        }
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }
</style>
